<template>
  <v-container :fluid="$vuetify.breakpoint.lgAndDown">
    <!-- Search Header -->
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-text>
            <!-- Search Bar -->
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="searchQuery"
                  label="Rechercher par ID de commande, Fournisseur, Code projet, Articles..."
                  prepend-inner-icon="mdi-magnify"
                  outlined
                  clearable
                  @input="onSearchInput"
                  @keyup.enter="performSearch"
                ></v-text-field>
              </v-col>
            </v-row>
            <!-- Controls -->
            <v-row>
              <v-col cols="12" sm="6">
                <v-btn
                  color="default"
                  outlined
                  @click="showAdvancedFilters = !showAdvancedFilters"
                >
                  {{ showAdvancedFilters ? "Masquer" : "Afficher" }} Filtres
                </v-btn>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="sortBy"
                  :items="sortOptions"
                  label="Trier par"
                  outlined
                  dense
                  @change="performSearch"
                ></v-select>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Advanced Filters Panel -->
    <v-expand-transition>
      <v-row v-show="showAdvancedFilters">
        <v-col cols="12">
          <v-card>
            <v-card-title>
              <v-icon left>mdi-filter</v-icon>
              Filtres Avancés
            </v-card-title>
            <v-card-text>
              <v-row>
                <!-- Status Filter -->
                <v-col cols="12" sm="6" md="4">
                  <v-autocomplete
                    v-model="selectedStatuses"
                    :items="availableStatuses"
                    label="Statut"
                    prepend-inner-icon="mdi-format-list-bulleted"
                    outlined
                    dense
                    clearable
                    multiple
                    chips
                    deletable-chips
                    small-chips
                    @change="performSearch"
                  ></v-autocomplete>
                </v-col>

                <!-- Year Filter -->
                <v-col cols="12" sm="6" md="4">
                  <v-autocomplete
                    v-model="filters.year"
                    :items="yearOptions"
                    label="Année"
                    prepend-inner-icon="mdi-calendar"
                    outlined
                    dense
                    multiple
                    chips
                    deletable-chips
                    small-chips
                    clearable
                    @change="onFilterChange"
                  ></v-autocomplete>
                </v-col>

                <!-- Currency Filter -->
                <v-col cols="12" sm="6" md="4">
                  <v-autocomplete
                    v-model="filters.currency"
                    :items="currencyOptions"
                    label="Devise"
                    prepend-inner-icon="mdi-cash-multiple"
                    outlined
                    dense
                    multiple
                    chips
                    deletable-chips
                    small-chips
                    clearable
                    @change="onFilterChange"
                  ></v-autocomplete>
                </v-col>

                <!-- Supplier Filter -->
                <v-col cols="12" sm="6" md="4">
                  <v-autocomplete
                    v-model="filters.supplier"
                    :items="supplierOptions"
                    label="Fournisseur"
                    prepend-inner-icon="mdi-domain"
                    outlined
                    dense
                    multiple
                    chips
                    deletable-chips
                    small-chips
                    clearable
                    @change="onFilterChange"
                  ></v-autocomplete>
                </v-col>

                <!-- Project Number Filter -->
                <v-col cols="12" sm="6" md="4">
                  <v-autocomplete
                    v-model="filters.projectNumber"
                    :items="projectNumberOptions"
                    label="Code projet"
                    prepend-inner-icon="mdi-folder-outline"
                    outlined
                    dense
                    clearable
                    multiple
                    chips
                    deletable-chips
                    small-chips
                    @change="onFilterChange"
                  ></v-autocomplete>
                </v-col>

                <!-- Client Number Filter -->
                <v-col cols="12" sm="6" md="4">
                  <v-autocomplete
                    v-model="filters.clientNumber"
                    :items="clientNumberOptions"
                    label="Numéro client"
                    prepend-inner-icon="mdi-account-outline"
                    outlined
                    dense
                    clearable
                    multiple
                    chips
                    deletable-chips
                    small-chips
                    @change="onFilterChange"
                  ></v-autocomplete>
                </v-col>

                <!-- Items Filter -->
                <v-col cols="12" sm="6" md="4">
                  <v-text-field
                    v-model="filters.items"
                    label="Articles (description, référence, code...)"
                    prepend-inner-icon="mdi-package-variant"
                    outlined
                    dense
                    clearable
                    @input="debounceSearch"
                  ></v-text-field>
                </v-col>

                <!-- Account Filter -->
                <v-col cols="12" sm="6" md="4">
                  <v-autocomplete
                    v-model="filters.account"
                    :items="accountOptions"
                    label="Compte"
                    prepend-inner-icon="mdi-bank"
                    outlined
                    dense
                    multiple
                    chips
                    deletable-chips
                    small-chips
                    clearable
                    @change="onFilterChange"
                  ></v-autocomplete>
                </v-col>

                <!-- Cost Center Filter -->
                <v-col cols="12" sm="6" md="4">
                  <v-autocomplete
                    v-model="filters.costCenter"
                    :items="costCenterOptions"
                    label="Centre de coûts"
                    prepend-inner-icon="mdi-office-building"
                    outlined
                    dense
                    multiple
                    chips
                    deletable-chips
                    small-chips
                    clearable
                    @change="onFilterChange"
                  ></v-autocomplete>
                </v-col>

                <!-- Date Range Filters -->
                <v-col cols="12" sm="6" md="4">
                  <v-menu
                    v-model="dateFromMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :value="formatDateInput(filters.createdAfter)"
                        label="Date de création - Depuis"
                        prepend-inner-icon="mdi-calendar-range"
                        readonly
                        outlined
                        dense
                        clearable
                        v-bind="attrs"
                        v-on="on"
                        @click:clear="
                          filters.createdAfter = '';
                          performSearch();
                        "
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="filters.createdAfter"
                      locale="fr-FR"
                      :max="filters.createdBefore || undefined"
                      @input="
                        dateFromMenu = false;
                        performSearch();
                      "
                    ></v-date-picker>
                  </v-menu>
                </v-col>

                <v-col cols="12" sm="6" md="4">
                  <v-menu
                    v-model="dateToMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :value="formatDateInput(filters.createdBefore)"
                        label="Date de création - Jusqu'à"
                        prepend-inner-icon="mdi-calendar-range"
                        readonly
                        outlined
                        dense
                        clearable
                        v-bind="attrs"
                        v-on="on"
                        @click:clear="
                          filters.createdBefore = '';
                          performSearch();
                        "
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="filters.createdBefore"
                      locale="fr-FR"
                      :min="filters.createdAfter || undefined"
                      @input="
                        dateToMenu = false;
                        performSearch();
                      "
                    ></v-date-picker>
                  </v-menu>
                </v-col>

                <!-- Value Range Filters -->
                <v-col cols="12" sm="6" md="4">
                  <v-text-field
                    v-model="filters.minValue"
                    label="Valeur minimale"
                    prepend-inner-icon="mdi-arrow-bottom-right"
                    type="number"
                    outlined
                    dense
                    clearable
                    @input="debounceSearch"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" sm="6" md="4">
                  <v-text-field
                    v-model="filters.maxValue"
                    label="Valeur maximale"
                    prepend-inner-icon="mdi-arrow-top-right"
                    type="number"
                    outlined
                    dense
                    clearable
                    @input="debounceSearch"
                  ></v-text-field>
                </v-col>
              </v-row>

              <!-- Filter Actions -->
              <v-row>
                <v-col cols="12" class="text-center">
                  <v-btn
                    color="primary"
                    outlined
                    @click="applyFilters"
                    class="mr-2"
                  >
                    <v-icon left>mdi-magnify</v-icon>
                    Rechercher
                  </v-btn>
                  <v-btn
                    color="secondary"
                    outlined
                    @click="clearAllFilters"
                    class="mr-2"
                  >
                    <v-icon left>mdi-filter-remove</v-icon>
                    Effacer tout
                  </v-btn>
                  <v-btn color="success" outlined @click="exportResults">
                    <v-icon left>mdi-file-excel</v-icon>
                    Exporter Excel
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-expand-transition>

    <!-- Active Filters Chips -->
    <v-row v-if="hasActiveFilters">
      <v-col cols="12">
        <v-card>
          <v-card-text>
            <div class="text-subtitle-2 mb-2">Filtres actifs :</div>
            <v-chip
              v-for="tag in activeFilterTags"
              :key="tag.key"
              close
              outlined
              class="mr-2 mb-2"
              @click:close="removeFilter(tag.key)"
            >
              {{ tag.label }}
            </v-chip>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Loading State -->
    <v-row v-if="loading">
      <v-col cols="12" class="text-center">
        <v-card>
          <v-card-text>
            <v-progress-circular
              indeterminate
              color="primary"
              size="64"
            ></v-progress-circular>
            <div class="mt-3">Recherche en cours...</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Results Summary -->
    <v-row v-if="!loading && searchResults.totalDocs > 0">
      <v-col cols="12">
        <v-card>
          <v-card-title>
            <v-icon left>mdi-chart-box</v-icon>
            Résultats de recherche
            <v-spacer></v-spacer>
            <v-btn-toggle v-model="currentViewIndex" mandatory>
              <v-btn :value="1">
                <v-icon>mdi-table</v-icon>
              </v-btn>
              <v-btn :value="0">
                <v-icon>mdi-card</v-icon>
              </v-btn>
            </v-btn-toggle>
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" sm="4" md="3">
                <v-card outlined>
                  <v-card-text class="text-center">
                    <div class="text-h4">
                      {{ searchResults.totalDocs.toLocaleString() }}
                    </div>
                    <div>Commandes trouvées</div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" sm="4" md="3">
                <v-card outlined>
                  <v-card-text class="text-center">
                    <div class="text-h4">{{ totalValue }}</div>
                    <div>Valeur totale</div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Results Content -->
    <v-row v-if="!loading && searchResults.docs.length > 0">
      <v-col cols="12">
        <!-- Cards View -->
        <div v-if="currentView === 'cards'">
          <v-row>
            <v-col
              v-for="order in searchResults.docs"
              :key="order._id"
              cols="12"
              sm="6"
              md="4"
              lg="3"
            >
              <v-card
                hover
                @click="viewOrder(order._id)"
                class="cursor-pointer"
              >
                <v-card-title class="text-h6">
                  E-{{ order.orderId }}-{{ order.creatorTla }}
                  <v-spacer></v-spacer>
                  <v-chip :color="getStatusColor(order.status)" outlined small>
                    {{ getStatusLabel(order.status) }}
                  </v-chip>
                </v-card-title>
                <v-card-text>
                  <div class="text-subtitle-1 font-weight-bold">
                    {{
                      order.supplier && order.supplier.name
                        ? order.supplier.name
                        : "N/A"
                    }}
                  </div>
                  <div class="text-h6 mt-2">
                    {{ formatCurrency(order.computedTotal, "CHF") }}
                  </div>
                  <v-divider class="my-2"></v-divider>
                  <div v-if="order.projectNumber" class="text-caption">
                    <v-icon small>mdi-folder-outline</v-icon>
                    {{ order.projectNumber }}
                  </div>
                  <div
                    v-if="order.createdBy && order.createdBy.name"
                    class="text-caption"
                  >
                    <v-icon small>mdi-account</v-icon>
                    {{ order.createdBy.name }}
                  </div>
                  <div class="text-caption">
                    <v-icon small>mdi-calendar</v-icon>
                    {{ formatDate(order.createdAt) }}
                  </div>
                </v-card-text>
                <v-card-actions>
                  <v-btn
                    small
                    text
                    color="primary"
                    @click.stop="editOrder(order._id)"
                  >
                    <v-icon small left>mdi-eye</v-icon>
                    Détail
                  </v-btn>
                  <v-btn
                    small
                    text
                    color="secondary"
                    @click.stop="downloadOrder(order._id)"
                  >
                    <v-icon small left>mdi-download</v-icon>
                    Télécharger
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>
        </div>

        <!-- Table View -->
        <v-card v-if="currentView === 'table'">
          <v-data-table
            :headers="tableHeaders"
            :items="searchResults.docs"
            :loading="loading"
            :items-per-page="-1"
            hide-default-footer
            class="elevation-1 clickable-rows"
            @click:row="(item) => viewOrder(item._id)"
          >
            <template v-slot:item.orderId="{ item }">
              E-{{ item.orderId }}-{{ item.creatorTla }}
            </template>

            <template v-slot:item.supplier="{ item }">
              {{
                item.supplier && item.supplier.name ? item.supplier.name : "N/A"
              }}
            </template>

            <template v-slot:item.status="{ item }">
              <v-chip :color="getStatusColor(item.status)" outlined small>
                {{ getStatusLabel(item.status) }}
              </v-chip>
            </template>

            <template v-slot:item.computedTotal="{ item }">
              <span class="font-weight-bold">
                {{ formatCurrency(item.computedTotal, "CHF") }}
              </span>
            </template>

            <template v-slot:item.createdAt="{ item }">
              {{ formatDate(item.createdAt) }}
            </template>

            <template v-slot:item.actions="{ item }">
              <v-btn
                icon
                small
                color="secondary"
                @click.stop="downloadOrder(item._id)"
              >
                <v-icon small>mdi-download</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Pagination -->
    <v-row v-if="!loading && searchResults.docs.length > 0">
      <v-col cols="12">
        <v-card>
          <v-card-text>
            <v-row align="center">
              <v-col cols="12" md="3">
                <v-select
                  v-model="pageSize"
                  :items="pageSizeOptions"
                  item-text="text"
                  item-value="value"
                  label="Résultats par page"
                  dense
                  @change="onPageSizeChange"
                  outlined
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-pagination
                  v-if="searchResults.totalPages > 1"
                  v-model="searchResults.page"
                  :length="searchResults.totalPages"
                  :total-visible="7"
                  class="outlined-pagination"
                  @input="goToPage"
                ></v-pagination>
              </v-col>
              <v-col cols="12" md="3" class="text-center">
                <span class="text-caption grey--text">
                  {{ (searchResults.page - 1) * pageSize + 1 }}-{{
                    Math.min(
                      searchResults.page * pageSize,
                      searchResults.totalDocs
                    )
                  }}
                  sur {{ searchResults.totalDocs }} résultats
                </span>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- No Results State -->
    <v-row v-if="!loading && searchResults.docs.length === 0 && hasSearched">
      <v-col cols="12">
        <v-card>
          <v-card-text class="text-center py-12">
            <v-icon size="64" color="grey lighten-2">mdi-magnify</v-icon>
            <div class="text-h5 mt-4">Aucune commande trouvée</div>
            <div class="text-subtitle-1 grey--text">
              Essayez de modifier vos critères de recherche
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Welcome State -->
    <v-row v-if="!loading && !hasSearched">
      <v-col cols="12">
        <v-card>
          <v-card-text class="text-center py-12">
            <v-icon size="64" color="primary">mdi-file-search-outline</v-icon>
            <div class="text-h5 mt-4">Recherche de Commandes</div>
            <div class="text-subtitle-1 grey--text">
              Utilisez les filtres ci-dessus pour rechercher des commandes
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
export default {
  name: "OrderSearchVuetify",
  data() {
    return {
      // Search and filters
      searchQuery: "",
      sortBy: "createdAt_desc",
      showAdvancedFilters: false,
      searchTimeout: null,
      hasSearched: false,
      allOrdersData: [], // Store all orders for dynamic filtering
      updatingDynamicOptions: false, // Flag to prevent infinite loops

      // Date pickers
      dateFromMenu: false,
      dateToMenu: false,

      // View controls
      currentView: "table",
      currentViewIndex: 1,

      selectedStatuses: [],

      // Filters
      filters: {
        year: [],
        currency: [],
        supplier: [],
        projectNumber: [],
        clientNumber: [],
        items: "",
        createdAfter: "",
        createdBefore: "",
        minValue: "",
        maxValue: "",
        account: [],
        costCenter: [],
      },

      // Results
      loading: false,
      pageSize: 10,
      searchResults: {
        docs: [],
        totalDocs: 0,
        totalPages: 0,
        page: 1,
        limit: 10,
      },

      // Statistics for all search results
      searchStatistics: {
        totalValue: 0,
        averageValue: 0,
        count: 0,
      },

      // Cache for multi-field search results
      multiFieldCache: {
        query: "",
        filters: "",
        sortBy: "",
        sortDesc: false,
        results: [],
      },

      // Available options
      availableStatuses: [],

      sortOptions: [
        { value: "createdAt_desc", text: "Date de création (Plus récente)" },
        { value: "createdAt_asc", text: "Date de création (Plus ancienne)" },
        {
          value: "computedTotal_desc",
          text: "Valeur de commande (Plus élevée)",
        },
        {
          value: "computedTotal_asc",
          text: "Valeur de commande (Plus faible)",
        },
        { value: "orderId_asc", text: "ID de commande" },
        { value: "supplier_asc", text: "Fournisseur" },
        { value: "status_asc", text: "Statut" },
      ],

      yearOptions: [],

      currencyOptions: [],

      accountOptions: [],

      costCenterOptions: [],

      supplierOptions: [],

      projectNumberOptions: [],

      clientNumberOptions: [],

      pageSizeOptions: [
        { value: 5, text: "5 par page" },
        { value: 10, text: "10 par page" },
        { value: 25, text: "25 par page" },
        { value: 50, text: "50 par page" },
        { value: 100, text: "100 par page" },
      ],

      tableHeaders: [
        { text: "N° BC", value: "orderId", sortable: false },
        { text: "Fournisseur", value: "supplier", sortable: false },
        { text: "Statut", value: "status", sortable: false },
        { text: "Montant", value: "computedTotal", sortable: false },
        { text: "Année", value: "year", sortable: false },
        { text: "Devise", value: "currency", sortable: false },
        { text: "Date de création", value: "createdAt", sortable: false },
        {
          text: "No et nom du projet",
          value: "projectNumber",
          sortable: false,
        },
        { text: "Actions", value: "actions", sortable: false, align: "center" },
      ],
    };
  },

  computed: {
    hasActiveFilters() {
      return (
        this.searchQuery ||
        this.selectedStatuses.length > 0 ||
        this.filters.year.length > 0 ||
        this.filters.currency.length > 0 ||
        this.filters.supplier.length > 0 ||
        this.filters.projectNumber.length > 0 ||
        this.filters.clientNumber.length > 0 ||
        this.filters.items ||
        this.filters.account.length > 0 ||
        this.filters.costCenter.length > 0 ||
        this.filters.createdAfter ||
        this.filters.createdBefore ||
        this.filters.minValue ||
        this.filters.maxValue
      );
    },

    activeFilterTags() {
      const tags = [];

      if (this.searchQuery) {
        tags.push({
          key: "search",
          label: `Recherche : "${this.searchQuery}"`,
        });
      }

      if (this.selectedStatuses.length > 0) {
        tags.push({
          key: "status",
          label: `Statut : ${this.selectedStatuses
            .map((s) => this.getStatusLabel(s))
            .join(", ")}`,
        });
      }

      if (this.filters.year.length > 0) {
        tags.push({
          key: "year",
          label: `Année : ${this.filters.year.join(", ")}`,
        });
      }

      if (this.filters.currency.length > 0) {
        tags.push({
          key: "currency",
          label: `Devise : ${this.filters.currency.join(", ")}`,
        });
      }

      if (this.filters.supplier.length > 0) {
        tags.push({
          key: "supplier",
          label: `Fournisseur : ${this.filters.supplier.join(", ")}`,
        });
      }

      if (this.filters.projectNumber.length > 0) {
        tags.push({
          key: "projectNumber",
          label: `Projet : ${this.filters.projectNumber.join(", ")}`,
        });
      }

      if (this.filters.clientNumber.length > 0) {
        tags.push({
          key: "clientNumber",
          label: `Client : ${this.filters.clientNumber.join(", ")}`,
        });
      }

      if (this.filters.items) {
        tags.push({
          key: "items",
          label: `Articles : "${this.filters.items}"`,
        });
      }

      if (this.filters.account.length > 0) {
        tags.push({
          key: "account",
          label: `Compte : ${this.filters.account.join(", ")}`,
        });
      }

      if (this.filters.costCenter.length > 0) {
        tags.push({
          key: "costCenter",
          label: `Centre de coûts : ${this.filters.costCenter.join(", ")}`,
        });
      }

      if (this.filters.createdAfter || this.filters.createdBefore) {
        const from = this.filters.createdAfter
          ? this.formatDateInput(this.filters.createdAfter)
          : "Début";
        const to = this.filters.createdBefore
          ? this.formatDateInput(this.filters.createdBefore)
          : "Fin";
        tags.push({ key: "dateRange", label: `Date : ${from} à ${to}` });
      }

      if (this.filters.minValue || this.filters.maxValue) {
        let label = "Valeur : ";
        if (this.filters.minValue && this.filters.maxValue) {
          label += `CHF ${parseInt(
            this.filters.minValue
          ).toLocaleString()} - CHF ${parseInt(
            this.filters.maxValue
          ).toLocaleString()}`;
        } else if (this.filters.minValue) {
          label += `≥ CHF ${parseInt(this.filters.minValue).toLocaleString()}`;
        } else {
          label += `≤ CHF ${parseInt(this.filters.maxValue).toLocaleString()}`;
        }
        tags.push({ key: "valueRange", label });
      }

      return tags;
    },

    totalValue() {
      return this.formatCurrency(this.searchStatistics.totalValue, "CHF");
    },

    averageValue() {
      return this.formatCurrency(this.searchStatistics.averageValue, "CHF");
    },
  },

  watch: {
    currentViewIndex(val) {
      this.currentView = val === 0 ? "cards" : "table";
      this.updateURL();
    },

    pageSize(newSize) {
      this.searchResults.limit = newSize;
    },

    // Dynamic filter watchers
    "filters.year": {
      handler() {
        if (!this.updatingDynamicOptions) {
          this.$nextTick(() => {
            this.updateDynamicOptions();
          });
        }
      },
      deep: true,
    },

    "filters.currency": {
      handler() {
        if (!this.updatingDynamicOptions) {
          this.$nextTick(() => {
            this.updateDynamicOptions();
          });
        }
      },
      deep: true,
    },

    "filters.supplier": {
      handler() {
        if (!this.updatingDynamicOptions) {
          this.$nextTick(() => {
            this.updateDynamicOptions();
          });
        }
      },
      deep: true,
    },

    "filters.account": {
      handler() {
        if (!this.updatingDynamicOptions) {
          this.$nextTick(() => {
            this.updateDynamicOptions();
          });
        }
      },
      deep: true,
    },

    "filters.costCenter": {
      handler() {
        if (!this.updatingDynamicOptions) {
          this.$nextTick(() => {
            this.updateDynamicOptions();
          });
        }
      },
      deep: true,
    },

    "filters.projectNumber": {
      handler() {
        if (!this.updatingDynamicOptions) {
          this.$nextTick(() => {
            this.updateDynamicOptions();
          });
        }
      },
      deep: true,
    },

    "filters.clientNumber": {
      handler() {
        if (!this.updatingDynamicOptions) {
          this.$nextTick(() => {
            this.updateDynamicOptions();
          });
        }
      },
      deep: true,
    },

    selectedStatuses: {
      handler() {
        if (!this.updatingDynamicOptions) {
          this.$nextTick(() => {
            this.updateDynamicOptions();
          });
        }
      },
      deep: true,
    },
  },

  methods: {
    // Dynamic filtering methods
    getFilteredOrdersForField(excludeField) {
      if (!this.allOrdersData.length) return [];

      let filteredOrders = [...this.allOrdersData];

      // First apply search query if it exists
      if (this.searchQuery && this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase().trim();
  
        filteredOrders = filteredOrders.filter((order) => {
          // Search in multiple fields
          const searchFields = [
            order.orderId?.toString(),
            order.supplier?.name,
            order.projectNumber,
            order.clientNumber,
            order.items,
            order.description,
            order.createdBy?.name,
          ];

          const matches = searchFields.some(
            (field) => field && field.toString().toLowerCase().includes(query)
          );

          return matches;
        });

      }

      // Apply all advanced filters EXCEPT the one we're calculating options for
      // This way when calculating supplier options, we don't filter by supplier selection

      if (excludeField !== "year" && this.filters.year.length > 0) {
        filteredOrders = filteredOrders.filter((order) => {
          // Check if order has direct year field
          if (order.year) {
            return this.filters.year.includes(order.year);
          }

          // Extract year from date fields
          const dateFields = ["createdAt", "poDate", "date", "orderDate"];
          for (const field of dateFields) {
            if (order[field]) {
              try {
                const date = new Date(order[field]);
                if (!isNaN(date.getTime())) {
                  const year = date.getFullYear().toString();
                  return this.filters.year.includes(year);
                }
              } catch (e) {
                // Invalid date, continue
              }
            }
          }
          return false;
        });
      }

      if (excludeField !== "currency" && this.filters.currency.length > 0) {
        filteredOrders = filteredOrders.filter((order) =>
          this.filters.currency.includes(order.currency)
        );
      }

      if (excludeField !== "supplier" && this.filters.supplier.length > 0) {
        filteredOrders = filteredOrders.filter((order) =>
          this.filters.supplier.includes(order.supplier?.name)
        );
      }

      if (excludeField !== "account" && this.filters.account.length > 0) {
        filteredOrders = filteredOrders.filter((order) =>
          this.filters.account.includes(order.account)
        );
      }

      if (excludeField !== "costCenter" && this.filters.costCenter.length > 0) {
        filteredOrders = filteredOrders.filter((order) =>
          this.filters.costCenter.includes(order.costCenter)
        );
      }

      if (
        excludeField !== "projectNumber" &&
        this.filters.projectNumber.length > 0
      ) {
        filteredOrders = filteredOrders.filter((order) =>
          this.filters.projectNumber.includes(order.projectNumber)
        );
      }

      if (
        excludeField !== "clientNumber" &&
        this.filters.clientNumber.length > 0
      ) {
        filteredOrders = filteredOrders.filter((order) =>
          this.filters.clientNumber.includes(order.clientNumber)
        );
      }

      if (excludeField !== "status" && this.selectedStatuses.length > 0) {
        filteredOrders = filteredOrders.filter((order) =>
          this.selectedStatuses.includes(order.status)
        );
      }

      return filteredOrders;
    },

    updateDynamicOptions() {
      if (!this.allOrdersData.length || this.updatingDynamicOptions) return;

      this.updatingDynamicOptions = true;

      try {
        // Update each dropdown by filtering orders excluding that field's current selection
        const filteredOrdersForYear = this.getFilteredOrdersForField("year");

        // Check if orders have 'year' field or if we need to extract from dates
        let yearsFromFilteredData;
        if (filteredOrdersForYear.length > 0 && filteredOrdersForYear[0].year) {
          // Direct year field exists
          yearsFromFilteredData = this.extractUniqueValuesFromFiltered(
            filteredOrdersForYear,
            "year",
            "year"
          );
        } else {
          // Extract years from date fields
          yearsFromFilteredData = this.extractYearsFromDates(
            filteredOrdersForYear
          );
        }

        this.yearOptions = yearsFromFilteredData.sort(
          (a, b) => parseInt(b.value) - parseInt(a.value)
        );
        this.currencyOptions = this.extractUniqueValuesFromFiltered(
          this.getFilteredOrdersForField("currency"),
          "currency",
          "currency"
        );
        this.supplierOptions = this.extractUniqueValuesFromFiltered(
          this.getFilteredOrdersForField("supplier"),
          "supplier.name",
          "supplier"
        );
        this.accountOptions = this.extractUniqueValuesFromFiltered(
          this.getFilteredOrdersForField("account"),
          "account",
          "account"
        );
        this.costCenterOptions = this.extractUniqueValuesFromFiltered(
          this.getFilteredOrdersForField("costCenter"),
          "costCenter",
          "costCenter"
        );
        this.projectNumberOptions = this.extractUniqueValuesFromFiltered(
          this.getFilteredOrdersForField("projectNumber"),
          "projectNumber",
          "projectNumber"
        );
        this.clientNumberOptions = this.extractUniqueValuesFromFiltered(
          this.getFilteredOrdersForField("clientNumber"),
          "clientNumber",
          "clientNumber"
        );
        this.availableStatuses = this.formatStatusOptions(
          this.extractUniqueValuesFromFiltered(
            this.getFilteredOrdersForField("status"),
            "status",
            "status"
          )
        );

      } finally {
        this.updatingDynamicOptions = false;
      }
    },

    extractUniqueValuesFromFiltered(orders, fieldPath, fieldType) {
      // Same logic as extractUniqueValues but for filtered data
      const values = new Set();

      orders.forEach((order) => {
        let value;
        if (fieldPath.includes(".")) {
          const parts = fieldPath.split(".");
          value = parts.reduce((obj, part) => obj && obj[part], order);
        } else {
          value = order[fieldPath];
        }

        if (value != null && value !== "") {
          values.add(value);
        }
      });

      const sortedValues = Array.from(values).sort();

      if (fieldType === "status") {
        return sortedValues.map((status) => ({
          value: status,
          text: this.getStatusLabel(status),
        }));
      }

      return sortedValues.map((value) => ({
        value: value,
        text: value,
      }));
    },

    extractYearsFromDates(orders) {
      const years = new Set();

      orders.forEach((order) => {
        // Try different date fields that might contain the year
        const dateFields = ["createdAt", "poDate", "date", "orderDate"];

        for (const field of dateFields) {
          if (order[field]) {
            try {
              const date = new Date(order[field]);
              if (!isNaN(date.getTime())) {
                const year = date.getFullYear();
                if (year > 1900 && year < 2100) {
                  // Sanity check
                  years.add(year.toString());
                }
                break; // Found a valid date, no need to check other fields
              }
            } catch (e) {
              // Invalid date, continue to next field
            }
          }
        }
      });

      return Array.from(years).map((year) => ({
        value: year,
        text: year,
      }));
    },


    // Search and filtering
    onSearchInput() {
      // If search is cleared, update dynamic options immediately
      if (this.searchQuery === "") {
        this.updateDynamicOptions();
      }

      this.debounceSearch();
    },

    debounceSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        // Clear cache when search query changes
        this.clearMultiFieldCache();

        // Update dynamic options when search changes
        this.updateDynamicOptions();

        // Only search if we have some text or filters
        if (this.searchQuery.length >= 2 || this.hasActiveFilters) {
          this.performSearch();
        } else if (this.searchQuery.length === 0) {
          // When search is cleared, show all orders (same behavior as "Effacer tout")
          this.updateURL();
          this.performSearch();
        }
      }, 300);
    },

    async performSearch() {
      this.loading = true;
      this.hasSearched = true;
      this.updateURL();

      try {
        const [sortBy, sortDesc] = this.parseSortBy();

        // If we have a search query, always try multiple search strategies
        if (this.searchQuery) {
          // Get additional filters (excluding the main search query)
          const additionalFilters = this.buildAdditionalFilters();

          let allResults;
          if (
            this.multiFieldCache.query === this.searchQuery &&
            this.multiFieldCache.filters ===
              JSON.stringify(additionalFilters) &&
            this.multiFieldCache.sortBy === sortBy &&
            this.multiFieldCache.sortDesc === sortDesc
          ) {
            allResults = this.multiFieldCache.results;
          } else {
            allResults = await this.getAllMultiFieldResults(
              this.searchQuery,
              additionalFilters,
              sortBy,
              sortDesc
            );

            // Cache the results
            this.multiFieldCache = {
              query: this.searchQuery,
              filters: JSON.stringify(additionalFilters),
              sortBy,
              sortDesc,
              results: allResults,
            };
          }

          // Apply pagination to the combined results
          const startIndex = (this.searchResults.page - 1) * this.pageSize;
          const endIndex = startIndex + this.pageSize;
          const paginatedResults = allResults.slice(startIndex, endIndex);

          // Calculate statistics from all results
          this.calculateStatistics(allResults);

          this.searchResults = {
            docs: this.transformOrderStatuses(paginatedResults),
            totalDocs: allResults.length,
            totalPages: Math.ceil(allResults.length / this.pageSize),
            page: this.searchResults.page,
            limit: this.pageSize,
          };
        } else {
          // Use regular search with filters only (no search query)
          // If no filters at all, show all orders by default
          const filters = this.buildApiFilters();
          const requestPayload = {
            filters,
            page: this.searchResults.page,
            limit: this.pageSize,
            sortBy,
            sortDesc,
          };


          // Handle multiple filters by making separate API calls and combining results
          if (this.hasMultipleValueFilters()) {
            const allResults = await this.searchMultipleFilters(
              filters,
              sortBy,
              sortDesc
            );

            // Apply pagination to the combined results
            const startIndex = (this.searchResults.page - 1) * this.pageSize;
            const endIndex = startIndex + this.pageSize;
            const paginatedResults = allResults.slice(startIndex, endIndex);

            // Calculate statistics from all results
            this.calculateStatistics(allResults);

            this.searchResults = {
              docs: this.transformOrderStatuses(paginatedResults),
              totalDocs: allResults.length,
              totalPages: Math.ceil(allResults.length / this.pageSize),
              page: this.searchResults.page,
              limit: this.pageSize,
            };
          } else {
            // No year filter - use regular search
            const [pageResponse, statsResponse] = await Promise.all([
              this.$http.post("/orders/search/advanced", requestPayload),
              this.$http.post("/orders/search/advanced", {
                ...requestPayload,
                page: 1,
                limit: 10000, // Large limit to get all results for statistics
              }),
            ]);

            this.searchResults = {
              ...pageResponse.data,
              docs: this.transformOrderStatuses(pageResponse.data.docs || []),
            };
            // Calculate statistics from all results
            this.calculateStatistics(
              this.transformOrderStatuses(statsResponse.data.docs || [])
            );
          }
          this.hasSearched = true; // Mark as searched so results show
        }

      } catch (error) {
        this.searchResults = {
          docs: [],
          totalDocs: 0,
          totalPages: 0,
          page: 1,
          limit: this.pageSize,
        };
        // Reset statistics on error
        this.searchStatistics = {
          totalValue: 0,
          averageValue: 0,
          count: 0,
        };
      } finally {
        this.loading = false;
      }
    },

    hasMultipleValueFilters() {
      return (
        this.filters.year.length > 0 ||
        this.filters.currency.length > 0 ||
        this.filters.supplier.length > 0 ||
        this.filters.account.length > 0 ||
        this.filters.costCenter.length > 0 ||
        this.filters.projectNumber.length > 0 ||
        this.filters.clientNumber.length > 0
      );
    },

    async searchMultipleFilters(baseFilters, sortBy, sortDesc) {
      try {
        // Create an array of all filter combinations to search
        const searchCombinations = this.generateFilterCombinations(baseFilters);

        // Make parallel requests for each combination
        const promises = searchCombinations.map((filters) => {
          return this.$http.post("/orders/search/advanced", {
            filters,
            page: 1,
            limit: 10000,
            sortBy,
            sortDesc,
          });
        });

        const responses = await Promise.all(promises);

        // Combine all results
        const allResults = [];
        responses.forEach((response) => {
          if (response.data.docs) {
            allResults.push(...this.transformOrderStatuses(response.data.docs));
          }
        });

        // Remove duplicates
        const uniqueResults = allResults.filter(
          (item, index, arr) =>
            arr.findIndex((t) => t._id === item._id) === index
        );

        // Sort the combined results
        uniqueResults.sort((a, b) => {
          const getValue = (obj, field) => {
            switch (field) {
              case "createdAt":
                return new Date(obj.createdAt);
              case "computedTotal":
                return obj.computedTotal || 0;
              case "orderId":
                return obj.orderId || "";
              case "supplier":
                return obj.supplier?.name || "";
              default:
                return obj[field] || "";
            }
          };

          const aVal = getValue(a, sortBy);
          const bVal = getValue(b, sortBy);

          if (sortDesc) {
            return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
          } else {
            return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
          }
        });

        return uniqueResults;
      } catch (error) {
        return [];
      }
    },

    generateFilterCombinations(baseFilters) {
      const combinations = [];

      // Collect all multi-value filters
      const multiFilters = {
        year: this.filters.year.length > 0 ? this.filters.year : [],
        currency: this.filters.currency.length > 0 ? this.filters.currency : [],
        supplier: this.filters.supplier.length > 0 ? this.filters.supplier : [],
        account: this.filters.account.length > 0 ? this.filters.account : [],
        costCenter:
          this.filters.costCenter.length > 0 ? this.filters.costCenter : [],
        projectNumber:
          this.filters.projectNumber.length > 0
            ? this.filters.projectNumber
            : [],
        clientNumber:
          this.filters.clientNumber.length > 0 ? this.filters.clientNumber : [],
      };

      // Fields that need to be sent as arrays for $in operator
      const arrayFields = ["currency"];
      // Fields that need to be sent as strings for $regex operator
      const stringFields = [
        "supplier",
        "account",
        "costCenter",
        "clientNumber",
        "projectNumber",
      ];
      // Fields that need to be sent as single values for exact match
      const singleValueFields = ["year"];

      // Helper function to format filter value based on field type
      const formatFilterValue = (filterType, value) => {
        if (arrayFields.includes(filterType)) {
          return [value]; // Send as array for $in
        } else if (stringFields.includes(filterType)) {
          return value; // Send as string for $regex
        } else if (singleValueFields.includes(filterType)) {
          return value; // Send as single value for exact match
        } else {
          return value; // Default to single value
        }
      };

      // Generate cartesian product of all filter combinations
      const filterEntries = Object.entries(multiFilters).filter(
        ([, values]) => values.length > 0
      );

      if (filterEntries.length === 0) {
        // No multi-value filters, return base filters
        combinations.push(baseFilters);
      } else {
        // Generate all combinations using cartesian product
        const generateCombinations = (
          entries,
          currentFilters = {},
          index = 0
        ) => {
          if (index === entries.length) {
            combinations.push({ ...baseFilters, ...currentFilters });
            return;
          }

          const [filterType, values] = entries[index];
          values.forEach((value) => {
            const formattedValue = formatFilterValue(filterType, value);
            generateCombinations(
              entries,
              { ...currentFilters, [filterType]: formattedValue },
              index + 1
            );
          });
        };

        generateCombinations(filterEntries);
      }

      return combinations.filter((combo) => Object.keys(combo).length > 0);
    },

    async searchMultipleYears(baseFilters, sortBy, sortDesc) {
      try {

        // Make parallel requests for each year
        const yearPromises = this.filters.year.map((year) => {
          const filtersWithYear = { ...baseFilters, year: year };

          return this.$http.post("/orders/search/advanced", {
            filters: filtersWithYear,
            page: 1,
            limit: 10000, // Get all results for this year
            sortBy,
            sortDesc,
          });
        });

        const responses = await Promise.all(yearPromises);

        // Combine all results
        const allResults = [];
        responses.forEach((response) => {
          if (response.data.docs) {
            allResults.push(...this.transformOrderStatuses(response.data.docs));
          }
        });

        // Remove duplicates (in case an order appears in multiple years somehow)
        const uniqueResults = allResults.filter(
          (item, index, arr) =>
            arr.findIndex((t) => t._id === item._id) === index
        );

        // Sort the combined results
        uniqueResults.sort((a, b) => {
          const getValue = (obj, field) => {
            switch (field) {
              case "createdAt":
                return new Date(obj.createdAt);
              case "computedTotal":
                return obj.computedTotal || 0;
              case "orderId":
                return obj.orderId || "";
              case "supplier":
                return obj.supplier?.name || "";
              default:
                return obj[field] || "";
            }
          };

          const aVal = getValue(a, sortBy);
          const bVal = getValue(b, sortBy);

          if (sortDesc) {
            return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
          } else {
            return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
          }
        });

        return uniqueResults;
      } catch (error) {
        return [];
      }
    },

    async searchByField(fieldName, query, sortBy, sortDesc) {
      try {
        const filters = {};
        filters[fieldName] = query;

        const response = await this.$http.post("/orders/search/advanced", {
          filters,
          page: 1,
          limit: 50, // Get more results for combining
          sortBy,
          sortDesc,
        });

        return this.transformOrderStatuses(response.data.docs || []);
      } catch (error) {
        return [];
      }
    },

    async searchByFieldWithFilters(
      fieldName,
      query,
      additionalFilters,
      sortBy,
      sortDesc
    ) {
      try {
        const filters = { ...additionalFilters };
        filters[fieldName] = query;


        const response = await this.$http.post("/orders/search/advanced", {
          filters,
          page: 1,
          limit: 50, // Get more results for combining
          sortBy,
          sortDesc,
        });

        return this.transformOrderStatuses(response.data.docs || []);
      } catch (error) {
        return [];
      }
    },

    async getAllMultiFieldResults(query, additionalFilters, sortBy, sortDesc) {
      try {
        // Get results from all search fields with higher limits
        const [orderIdResults, supplierResults, projectResults, itemsResults] =
          await Promise.all([
            this.searchByFieldWithFilters(
              "orderId",
              query,
              additionalFilters,
              sortBy,
              sortDesc
            ),
            this.searchByFieldWithFilters(
              "supplier",
              query,
              additionalFilters,
              sortBy,
              sortDesc
            ),
            this.searchByFieldWithFilters(
              "projectNumber",
              query,
              additionalFilters,
              sortBy,
              sortDesc
            ),
            this.searchByFieldWithFilters(
              "items",
              query,
              additionalFilters,
              sortBy,
              sortDesc
            ),
          ]);


        // Combine and deduplicate results
        const combinedResults = [
          ...orderIdResults,
          ...supplierResults,
          ...projectResults,
          ...itemsResults,
        ];
        const uniqueResults = combinedResults.filter(
          (item, index, arr) =>
            arr.findIndex((t) => t._id === item._id) === index
        );

        // Sort the combined results according to the sortBy criteria
        uniqueResults.sort((a, b) => {
          const getValue = (obj, field) => {
            switch (field) {
              case "createdAt":
                return new Date(obj.createdAt);
              case "computedTotal":
                return obj.computedTotal || 0;
              case "orderId":
                return obj.orderId || "";
              case "supplier":
                return obj.supplier?.name || "";
              default:
                return obj[field] || "";
            }
          };

          const aVal = getValue(a, sortBy);
          const bVal = getValue(b, sortBy);

          if (sortDesc) {
            return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
          } else {
            return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
          }
        });

        return uniqueResults;
      } catch (error) {
        return [];
      }
    },

    buildAdditionalFilters() {
      const apiFilters = {};

      if (this.selectedStatuses.length > 0) {
        apiFilters.status = this.selectedStatuses;
      }

      // Skip multiple-value filters - they're handled separately in searchMultipleFilters

      // Skip projectNumber and clientNumber - handled by searchMultipleFilters

      if (this.filters.items) {
        apiFilters.items = this.filters.items;
      }

      // Skip account and costCenter - handled by searchMultipleFilters

      // Don't add year to apiFilters here - we'll handle multiple years separately
      // if (this.filters.year.length > 0) {
      //   apiFilters.year = this.filters.year[0];
      // }

      if (this.filters.createdAfter) {
        apiFilters.createdAfter = this.filters.createdAfter;
      }

      if (this.filters.createdBefore) {
        apiFilters.createdBefore = this.filters.createdBefore;
      }

      if (this.filters.minValue) {
        apiFilters.minValue = this.filters.minValue;
      }

      if (this.filters.maxValue) {
        apiFilters.maxValue = this.filters.maxValue;
      }

      // Skip currency - handled by searchMultipleFilters

      return apiFilters;
    },

    clearMultiFieldCache() {
      this.multiFieldCache = {
        query: "",
        filters: "",
        sortBy: "",
        sortDesc: false,
        results: [],
      };
    },

    hasOtherFilters() {
      return (
        this.selectedStatuses.length > 0 ||
        this.filters.year.length > 0 ||
        this.filters.currency.length > 0 ||
        this.filters.supplier.length > 0 ||
        this.filters.projectNumber.length > 0 ||
        this.filters.clientNumber.length > 0 ||
        this.filters.account.length > 0 ||
        this.filters.costCenter.length > 0 ||
        this.filters.createdAfter ||
        this.filters.createdBefore ||
        this.filters.minValue ||
        this.filters.maxValue
      );
    },

    buildApiFilters() {
      const apiFilters = {};

      if (this.searchQuery) {
        // For now, let's just use orderId since that works
        // We'll implement multi-field search in performSearch method
        apiFilters.orderId = this.searchQuery;
      }

      if (this.selectedStatuses.length > 0) {
        apiFilters.status = this.selectedStatuses;
      }

      // Skip multiple-value filters - they're handled in searchMultipleFilters

      // Skip projectNumber and clientNumber - handled by searchMultipleFilters

      if (this.filters.items) {
        apiFilters.items = this.filters.items;
      }

      // Skip account and costCenter - handled by searchMultipleFilters

      // Don't add year to apiFilters here - we'll handle multiple years separately
      // if (this.filters.year.length > 0) {
      //   apiFilters.year = this.filters.year[0];
      // }

      if (this.filters.createdAfter) {
        apiFilters.createdAfter = this.filters.createdAfter;
      }

      if (this.filters.createdBefore) {
        apiFilters.createdBefore = this.filters.createdBefore;
      }

      if (this.filters.minValue) {
        apiFilters.minValue = this.filters.minValue;
      }

      if (this.filters.maxValue) {
        apiFilters.maxValue = this.filters.maxValue;
      }

      // Skip currency - handled by searchMultipleFilters

      return apiFilters;
    },

    buildExportFilters() {
      const exportFilters = {};

      // Include ALL current search parameters for export
      if (this.searchQuery) {
        exportFilters.orderId = this.searchQuery;
      }

      if (this.selectedStatuses.length > 0) {
        exportFilters.status = this.selectedStatuses;
      }

      if (this.filters.year.length > 0) {
        exportFilters.year = this.filters.year;
      }

      if (this.filters.currency.length > 0) {
        exportFilters.currency = this.filters.currency;
      }

      if (this.filters.supplier.length > 0) {
        exportFilters.supplier = this.filters.supplier;
      }

      if (this.filters.projectNumber.length > 0) {
        exportFilters.projectNumber = this.filters.projectNumber;
      }

      if (this.filters.clientNumber.length > 0) {
        exportFilters.clientNumber = this.filters.clientNumber;
      }

      if (this.filters.account.length > 0) {
        exportFilters.account = this.filters.account;
      }

      if (this.filters.costCenter.length > 0) {
        exportFilters.costCenter = this.filters.costCenter;
      }

      if (this.filters.items) {
        exportFilters.items = this.filters.items;
      }

      if (this.filters.createdAfter) {
        exportFilters.createdAfter = this.filters.createdAfter;
      }

      if (this.filters.createdBefore) {
        exportFilters.createdBefore = this.filters.createdBefore;
      }

      if (this.filters.minValue) {
        exportFilters.minValue = this.filters.minValue;
      }

      if (this.filters.maxValue) {
        exportFilters.maxValue = this.filters.maxValue;
      }

      return exportFilters;
    },

    parseSortBy() {
      const [field, direction] = this.sortBy.split("_");
      return [field, direction === "desc"];
    },

    // Filter actions
    applyFilters() {
      this.performSearch();
      if (this.$vuetify.breakpoint.smAndDown) {
        this.showAdvancedFilters = false;
      }
    },

    clearAllFilters() {
      this.searchQuery = "";
      this.selectedStatuses = [];
      this.filters = {
        year: [],
        currency: [],
        supplier: [],
        projectNumber: [],
        clientNumber: [],
        items: "",
        account: [],
        costCenter: [],
        createdAfter: "",
        createdBefore: "",
        minValue: "",
        maxValue: "",
      };
      this.searchResults.page = 1;
      this.hasSearched = false;
      // Clear the cache when filters change
      this.clearMultiFieldCache();
      this.updateURL();
      // Trigger a search to refresh results with cleared filters
      this.performSearch();
    },

    removeFilter(key) {
      // Clear cache when filters change
      this.clearMultiFieldCache();

      switch (key) {
        case "search":
          this.searchQuery = "";
          break;
        case "status":
          this.selectedStatuses = [];
          break;
        case "year":
          this.filters.year = [];
          break;
        case "currency":
          this.filters.currency = [];
          break;
        case "supplier":
          this.filters.supplier = [];
          break;
        case "projectNumber":
          this.filters.projectNumber = [];
          break;
        case "clientNumber":
          this.filters.clientNumber = [];
          break;
        case "items":
          this.filters.items = "";
          break;
        case "account":
          this.filters.account = [];
          break;
        case "costCenter":
          this.filters.costCenter = [];
          break;
        case "dateRange":
          this.filters.createdAfter = "";
          this.filters.createdBefore = "";
          break;
        case "valueRange":
          this.filters.minValue = "";
          this.filters.maxValue = "";
          break;
      }
      this.performSearch();
    },

    // Order actions
    viewOrder(orderId) {
      this.$router.push(`/orders/${orderId}`);
    },

    editOrder(orderId) {
      this.$router.push(`/orders/${orderId}`);
    },

    async downloadOrder(orderId) {
      try {
        // Fetch full order details to get binder information
        // Search results don't include binder/validatedBinder fields
        const orderResponse = await fetch(`${process.env.VUE_APP_API_URI}/orders/${orderId}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
            "Content-Type": "application/json",
          },
        });

        if (!orderResponse.ok) {
          throw new Error(`Erreur lors de la récupération de la commande: ${orderResponse.status}`);
        }

        const order = await orderResponse.json();

        // Use validatedBinder if it exists, otherwise use binder
        const binderFileName = order.validatedBinder || order.binder;

        if (!binderFileName) {
          throw new Error("Aucun classeur disponible pour cette commande");
        }

        const downloadUrl = `${process.env.VUE_APP_API_URI}/files/${binderFileName}`;

        // Use fetch for binary file download
        const response = await fetch(downloadUrl, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || `HTTP error! status: ${response.status}`);
        }

        // Get the blob data
        const blob = await response.blob();

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        // Use E-orderId-creatorTla format for filename
        const filename = `E-${order.orderId}-${order.creatorTla}.pdf`;

        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();

        // Cleanup
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        this.$toast.error("Erreur lors du téléchargement du classeur");
      }
    },

    async exportResults() {
      try {
        // Build complete filters for export (including all current search parameters)
        const filters = this.buildExportFilters();
        const params = new URLSearchParams();

        for (const key in filters) {
          if (filters[key] !== null && filters[key] !== undefined) {
            if (Array.isArray(filters[key])) {
              params.append(key, filters[key].join(","));
            } else {
              params.append(key, filters[key]);
            }
          }
        }

        // Add format parameter for Excel export
        params.append("format", "excel");

        const exportUrl = `${
          process.env.VUE_APP_API_URI
        }/orders/search/export?${params.toString()}`;

        // Use fetch for binary file download
        const response = await fetch(exportUrl, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
            "Content-Type": "application/json",
          },
        });


        if (!response.ok) {
          throw new Error("Export failed");
        }

        // Get the blob data
        const blob = await response.blob();

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        // Get filename from response headers or use default
        const contentDisposition = response.headers.get("content-disposition");
        let filename = "orders-export.xlsx";
        if (contentDisposition) {
          const filenameMatch =
            contentDisposition.match(/filename="?([^"]+)"?/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }

        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();

        // Cleanup
        link.remove();
        window.URL.revokeObjectURL(url);

        this.$toast && this.$toast.success("Export Excel téléchargé");
      } catch (error) {
        this.$toast && this.$toast.error("Échec de l'export Excel");
      }
    },

    // Helpers
    getStatusLabel(value) {
      const status = this.availableStatuses.find((s) => s.value === value);
      return status ? status.text : value;
    },

    getStatusColor(status) {
      const colors = {
        draft: "red",
        processing: "orange",
        "to-order": "blue",
        "to-order-contractor": "blue",
        "ordered-supplier": "green",
        deleted: "grey",
      };
      return colors[status] || "grey";
    },

    formatCurrency(value, currency) {
      const formatter = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currency || "CHF",
      });
      return formatter.format(value || 0);
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString("de-CH", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    },

    formatDateInput(dateString) {
      if (!dateString) return "";
      return new Date(dateString).toLocaleDateString("de-CH", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    },

    // URL parameter handling
    updateURL() {
      const params = new URLSearchParams();

      if (this.searchQuery) params.set("search", this.searchQuery);
      if (this.selectedStatuses.length > 0)
        params.set("status", this.selectedStatuses.join(","));
      if (this.filters.year.length > 0)
        params.set("year", this.filters.year.join(","));
      if (this.filters.currency.length > 0)
        params.set("currency", this.filters.currency.join(","));
      if (this.filters.supplier.length > 0)
        params.set("supplier", this.filters.supplier.join(","));
      if (this.filters.projectNumber.length > 0)
        params.set("projectNumber", this.filters.projectNumber.join(","));
      if (this.filters.clientNumber.length > 0)
        params.set("clientNumber", this.filters.clientNumber.join(","));
      if (this.filters.items) params.set("items", this.filters.items);
      if (this.filters.account.length > 0)
        params.set("account", this.filters.account.join(","));
      if (this.filters.costCenter.length > 0)
        params.set("costCenter", this.filters.costCenter.join(","));
      if (this.filters.createdAfter)
        params.set("createdAfter", this.filters.createdAfter);
      if (this.filters.createdBefore)
        params.set("createdBefore", this.filters.createdBefore);
      if (this.filters.minValue) params.set("minValue", this.filters.minValue);
      if (this.filters.maxValue) params.set("maxValue", this.filters.maxValue);
      if (this.searchResults.page > 1)
        params.set("page", this.searchResults.page);
      if (this.pageSize !== 10) params.set("pageSize", this.pageSize);
      if (this.currentView !== "table") params.set("view", this.currentView);
      if (this.sortBy !== "createdAt_desc") params.set("sort", this.sortBy);

      const newUrl = params.toString()
        ? `${this.$route.path}?${params.toString()}`
        : this.$route.path;

      // Only update URL if it actually changed
      const currentUrl = window.location.pathname + window.location.search;
      if (newUrl !== currentUrl) {
        this.$router.replace(newUrl).catch((err) => {
          // Ignore NavigationDuplicated errors
          if (err.name !== "NavigationDuplicated") {
            throw err;
          }
        });
      }
    },

    initializeFromURL() {
      const params = new URLSearchParams(window.location.search);

      params.forEach((value, key) => {
        switch (key) {
          case "search":
            this.searchQuery = value;
            this.searchInput = value;
            break;
          case "year":
            this.filters.year = value.split(",");
            break;
          case "currency":
            this.filters.currency = value.split(",");
            break;
          case "supplier":
            this.filters.supplier = value.split(",");
            break;
          case "projectNumber":
            this.filters.projectNumber = value.split(",");
            break;
          case "clientNumber":
            this.filters.clientNumber = value.split(",");
            break;
          case "items":
            this.filters.items = value;
            break;
          case "account":
            this.filters.account = value.split(",");
            break;
          case "costCenter":
            this.filters.costCenter = value.split(",");
            break;
          case "status":
            this.selectedStatuses = value.split(",");
            break;
          case "createdAfter":
            this.filters.createdAfter = value;
            break;
          case "createdBefore":
            this.filters.createdBefore = value;
            break;
          case "minValue":
            this.filters.minValue = value;
            break;
          case "maxValue":
            this.filters.maxValue = value;
            break;
          case "page":
            this.searchResults.page = parseInt(value) || 1;
            break;
          case "pageSize":
            this.pageSize = parseInt(value) || 10;
            this.searchResults.limit = this.pageSize;
            break;
          case "view":
            this.currentView = value;
            this.currentViewIndex = value === "table" ? 1 : 0;
            break;
          case "sort":
            this.sortBy = value;
            break;
        }
      });

      if (params.toString()) {
        this.performSearch();
      }
    },

    // Pagination
    goToPage(page) {
      this.searchResults.page = page;
      this.updateURL();
      this.performSearch();
    },

    onPageSizeChange() {
      // Reset to first page when changing page size
      this.searchResults.page = 1;
      this.searchResults.limit = this.pageSize;
      this.clearMultiFieldCache();
      this.updateURL();
      this.performSearch();
    },

    // Statistics calculation
    calculateStatistics(allResults) {
      const total = allResults.reduce((sum, order) => {
        return sum + (order.computedTotal || 0);
      }, 0);

      const count = allResults.length;
      const average = count > 0 ? total / count : 0;

      this.searchStatistics = {
        totalValue: total,
        averageValue: Math.round(average),
        count: count,
      };
    },

    // Load all user's orders to populate dropdown options
    async loadUserOrderOptions() {
      try {
        // Get all user's orders without any filters

        // Get user orders using advanced search endpoint
        const response = await this.$http.post("/orders/search/advanced", {
          filters: {}, // No filters - get all user's orders
          page: 1,
          limit: 10000, // Large limit to get all orders
        });


        let allOrders = response.data.docs;

        // Transform status for orders with orderedAt date (like to-order workaround)
        allOrders = this.transformOrderStatuses(allOrders);

        // Populate all dropdown options
        this.availableStatuses = this.formatStatusOptions(
          this.extractUniqueValues(allOrders, "status", "status")
        );

        // For years, use both data from orders and provide reasonable defaults
        const yearsFromData = this.extractUniqueValues(
          allOrders,
          "year",
          "year"
        );
        const currentYear = new Date().getFullYear();
        const staticYears = [];

        // Generate year range from 2020 to current year + 1
        for (let year = 2020; year <= currentYear + 1; year++) {
          staticYears.push({ value: year.toString(), text: year.toString() });
        }

        // Combine static years with data years, remove duplicates
        const allYears = [...staticYears, ...yearsFromData];
        const uniqueYears = allYears
          .filter(
            (year, index, arr) =>
              arr.findIndex((y) => y.value === year.value) === index
          )
          .sort((a, b) => parseInt(b.value) - parseInt(a.value)); // Sort descending


        // Store all orders for dynamic filtering
        this.allOrdersData = allOrders;

        this.yearOptions = uniqueYears;
        this.currencyOptions = this.extractUniqueValues(
          allOrders,
          "currency",
          "currency"
        );
        this.supplierOptions = this.extractUniqueValues(
          allOrders,
          "supplier.name",
          "supplier"
        );
        this.projectNumberOptions = this.extractUniqueValues(
          allOrders,
          "projectNumber",
          "projectNumber"
        );
        this.clientNumberOptions = this.extractUniqueValues(
          allOrders,
          "clientNumber",
          "clientNumber"
        );
        this.accountOptions = this.extractUniqueValues(
          allOrders,
          "account",
          "account"
        );
        this.costCenterOptions = this.extractUniqueValues(
          allOrders,
          "costCenter",
          "costCenter"
        );

        // Initialize dynamic filtering after loading all data
        this.$nextTick(() => {
          this.updateDynamicOptions();
        });
      } catch (error) {
        // Silently handle error loading order options
      }
    },

    // Extract unique values from orders for dropdown options
    // Helper function to transform order status based on orderedAt date
    transformOrderStatus(order) {
      if (
        order.orderedAt &&
        (order.status === "to-order" || order.status === "to-order-contractor")
      ) {
        order.status = "ordered";
      }
      return order;
    },

    // Helper function to transform multiple orders
    transformOrderStatuses(orders) {
      return orders.map((order) => this.transformOrderStatus({ ...order }));
    },

    extractUniqueValues(orders, fieldPath, fieldType) {
      const values = new Set();

      orders.forEach((order) => {
        let value;
        if (fieldPath.includes(".")) {
          // Handle nested fields like supplier.name
          const parts = fieldPath.split(".");
          value = parts.reduce((obj, part) => obj && obj[part], order);
        } else {
          value = order[fieldPath];
        }

        if (value != null && value !== "") {
          values.add(value);
        }
      });

      const sortedValues = Array.from(values).sort();

      // Special handling for status to get proper labels
      if (fieldType === "status") {
        return sortedValues.map((status) => ({
          value: status,
          text: this.getStatusLabel(status),
        }));
      }

      return sortedValues.map((value) => ({
        value: value,
        text: value,
      }));
    },

    // Format status options with proper labels
    formatStatusOptions(statusData) {
      const statusLabels = {
        draft: "Brouillon",
        processing: "En cours de validation",
        "to-order": "À commander",
        "to-order-contractor": "À commander (Contractant)",
        "ordered-supplier": "Commandé (Fournisseur)",
        ordered: "Commandé",
        deleted: "Supprimé",
      };

      return statusData.map((status) => ({
        value: status.value,
        text: statusLabels[status.value] || status.value,
      }));
    },

    // Handler for filter changes - just perform search
    onFilterChange() {
      this.performSearch();
    },
  },

  async mounted() {
    this.initializeFromURL();

    // Load dropdown options from user's orders (static, not dynamic)
    await this.loadUserOrderOptions();

    // If no URL parameters, load all orders by default
    const params = new URLSearchParams(window.location.search);
    if (!params.toString()) {
      this.performSearch();
    }
  },

  beforeUnmount() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.clickable-rows tbody tr {
  cursor: pointer !important;
}

.clickable-rows tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.04) !important;
}

.clickable-rows .v-data-table__wrapper tbody tr {
  cursor: pointer !important;
}

.clickable-rows .v-data-table__wrapper tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.04) !important;
}

/* More specific selectors for Vuetify */
.v-data-table.clickable-rows tbody tr {
  cursor: pointer !important;
}

.v-data-table.clickable-rows tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.04) !important;
}

.v-data-table.clickable-rows .v-data-table__wrapper tbody tr {
  cursor: pointer !important;
}

.v-data-table.clickable-rows .v-data-table__wrapper tbody tr td {
  cursor: pointer !important;
}

/* Deep selectors to penetrate Vuetify styles */
.clickable-rows >>> tbody tr {
  cursor: pointer !important;
}

.clickable-rows >>> tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.04) !important;
}

.clickable-rows >>> .v-data-table__wrapper tbody tr {
  cursor: pointer !important;
}

.clickable-rows >>> .v-data-table__wrapper tbody tr td {
  cursor: pointer !important;
}

/* Target chips and other components within clickable rows */
.clickable-rows >>> tbody tr td .v-chip {
  cursor: pointer !important;
}

.clickable-rows >>> tbody tr td * {
  cursor: pointer !important;
}

.outlined-pagination >>> .v-pagination__item,
.outlined-pagination >>> .v-pagination__navigation {
  border: 1px solid rgba(0, 0, 0, 0.26) !important;
  background-color: transparent !important;
  margin: 0 2px !important;
  box-shadow: none !important;
}

.outlined-pagination >>> .v-pagination__item--active {
  border: 2px solid #1976d2 !important;
  background-color: #1976d2 !important;
  color: white !important;
}

.outlined-pagination
  >>> .v-pagination__item:hover:not(.v-pagination__item--active),
.outlined-pagination >>> .v-pagination__navigation:hover {
  border-color: #1976d2 !important;
  background-color: rgba(25, 118, 210, 0.08) !important;
}

.outlined-pagination >>> .v-btn {
  border: 1px solid rgba(0, 0, 0, 0.26) !important;
  background-color: transparent !important;
  box-shadow: none !important;
}

.outlined-pagination >>> .v-btn--active {
  border: 2px solid #1976d2 !important;
  background-color: #1976d2 !important;
  color: white !important;
}

.v-card--hover {
  transition: transform 0.2s ease-in-out;
}

.v-card--hover:hover {
  transform: translateY(-2px);
}
</style>